"use client";

import React, { useEffect, useState, type FC } from "react";
import clsx from "clsx";

import { Dialog, DialogBody, DialogTitle } from "@/components/ui/dialog/Dialog";
import { useIsOsMode } from "@/components/ui/os-layout";
import TaskStepsProgress from "@/components/ui/task-steps-progress/TaskStepsProgress";
import { TaskStepList } from "@/components/ui/task-steps-progress/TaskStepList";
import { useTaskStream, type TaskStepWithDuration } from "@/hooks/useTaskStream";
import type { CaseAgenticMissionLink } from "@/services/api/case-checking";

import { missionStagesToTaskSteps } from "./missionStageProgress";

/** Live runner progress from `useCaseProgress` — avoids a second WebSocket subscription. */
export type CaseCheckMissionTaskStream = {
  steps: TaskStepWithDuration[];
  overallProgress: number;
  statusMessage: string;
  isComplete: boolean;
  hasFailed: boolean;
  isCancelled: boolean;
  isLoadingInitial: boolean;
};

export type MissionStagesCollapseMode = "hide" | "always";

type CaseCheckMissionProgressDialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  missionLink?: CaseAgenticMissionLink | null;
  taskId?: string | null;
  taskStream?: CaseCheckMissionTaskStream;
  /** When "hide", mission stages collapse while a task item group is expanded (default). */
  missionStagesCollapseMode?: MissionStagesCollapseMode;
};

export const CaseCheckMissionProgressDialog: FC<CaseCheckMissionProgressDialogProps> = ({
  open,
  onClose,
  title = "Case check progress",
  missionLink,
  taskId,
  taskStream: taskStreamFromParent,
  missionStagesCollapseMode = "hide",
}) => {
  const ownedTaskIds = open && taskId && !taskStreamFromParent ? [taskId] : [];
  const ownedStream = useTaskStream(ownedTaskIds, { stepTaskIds: ownedTaskIds, intervalMs: 2000 });
  const {
    steps: taskSteps,
    overallProgress,
    statusMessage,
    isComplete,
    hasFailed,
    isCancelled,
    isLoadingInitial,
  } = taskStreamFromParent ?? ownedStream;

  const showRunnerDetail = Boolean(open && taskId && (taskStreamFromParent || ownedTaskIds.length > 0));

  const isOsMode = useIsOsMode();
  const [taskItemsExpanded, setTaskItemsExpanded] = useState(false);
  const missionSteps = missionStagesToTaskSteps(missionLink?.stages, missionLink?.current_stage);
  const activeStageName = missionLink?.current_stage_display?.trim();
  const showTaskDetail = showRunnerDetail && (taskSteps.length > 0 || isLoadingInitial);
  const showMissionStages =
    missionSteps.length > 0 && (missionStagesCollapseMode === "always" || !taskItemsExpanded);

  useEffect(() => {
    if (!open) setTaskItemsExpanded(false);
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      className={clsx(
        "!flex flex-col",
        isOsMode
          ? "!max-h-[calc(100%-2rem)] !max-w-[calc(100%-2rem)]"
          : "!max-h-[min(32rem,calc(100vh-3rem))]",
      )}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogBody className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden !pb-4">
        {showMissionStages ? (
          <section className={clsx("flex min-h-0 flex-col", showTaskDetail ? "flex-[1.6]" : "flex-1")}>
            <h3 className="mb-2 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Mission stages
            </h3>
            <TaskStepList
              steps={missionSteps}
              className="min-h-0 flex-1 space-y-2 overflow-y-auto scrollbar-hide"
            />
          </section>
        ) : null}

        {showTaskDetail ? (
          <section className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <h3 className="mb-2 shrink-0 text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              {activeStageName ? `${activeStageName} detail` : "Current step detail"}
            </h3>
            <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
              <TaskStepsProgress
                title={activeStageName || "Processing"}
                steps={taskSteps}
                overallProgress={overallProgress}
                isComplete={isComplete}
                hasFailed={hasFailed}
                isCancelled={isCancelled}
                statusMessage={statusMessage}
                isLoadingInitial={isLoadingInitial}
                showTitle={false}
                fillContainer
                onAnyItemGroupExpandedChange={setTaskItemsExpanded}
              />
            </div>
          </section>
        ) : null}
      </DialogBody>
    </Dialog>
  );
};
